import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError, downloadFile } from "@/lib/api";
import TeamSelector from "@/components/TeamSelector";
import { toast } from "sonner";
import { Plus, X, Search, Archive, QrCode, Eye, EyeOff, Download, Star, Trash2, CheckSquare, Square, RotateCcw, AlertTriangle } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  graduation_year: "",
  primary_position: "",
  secondary_position: "",
  bats: "",
  throws: "",
  height_cm: "",
  weight_kg: "",
  school: "",
  current_team_id: "",
  date_of_birth: "",
  show_age_publicly: false,
  city: "",
  province: "",
  email: "",
  phone: "",
  profile_photo_url: "",
};

export default function AdminPlayers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [camps, setCamps] = useState([]);
  const [filters, setFilters] = useState({ q: "", graduation_year: "", primary_position: "", province: "", camp_id: "", status: "active" });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // {items, count, with_history} | null
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(async () => {
    const params = { status_filter: filters.status };
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === null || k === "status") return;
      if (k === "graduation_year") params.graduation_year = Number(v);
      else params[k] = v;
    });
    const { data } = await api.get("/admin/players", { params });
    setItems(data.items || []);
    setTotal(data.total || 0);
    setSelected(new Set());
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/camps", { params: { status_filter: "" } }).then(({ data }) => setCamps(data.items || []));
  }, []);

  const toggleFeatured = async (p) => {
    const next = !p.is_featured;
    setItems((arr) => arr.map((x) => (x.id === p.id ? { ...x, is_featured: next } : x)));
    try {
      await api.patch(`/players/${p.id}`, { is_featured: next });
      toast.success(next ? `${p.first_name} is now Featured` : `${p.first_name} removed from Featured`);
    } catch (e) {
      setItems((arr) => arr.map((x) => (x.id === p.id ? { ...x, is_featured: !next } : x)));
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const create = async () => {
    setError("");
    if (!form.first_name || !form.last_name) {
      setError("First and last name required");
      return;
    }
    setBusy(true);
    try {
      const raw = {
        ...form,
        graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      };
      const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== "" && v !== null));
      await api.post("/admin/players", payload);
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const allSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(items.map((p) => p.id))));
  const clearSelection = () => setSelected(new Set());
  const selectedIds = () => Array.from(selected);

  const bulkArchive = async (archived) => {
    const ids = selectedIds();
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/players/bulk-archive", { player_ids: ids, archived });
      toast.success(`${data.updated} player${data.updated === 1 ? "" : "s"} ${archived ? "archived" : "restored"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const openDeletePreview = async () => {
    const ids = selectedIds();
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/players/deletion-preview", { player_ids: ids });
      setConfirmText("");
      setDeleteModal(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load deletion preview");
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmDelete = async () => {
    const ids = deleteModal.items.map((i) => i.id);
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/players/bulk-delete", { player_ids: ids, confirm: confirmText });
      toast.success(`${data.deleted} player${data.deleted === 1 ? "" : "s"} permanently deleted`);
      setDeleteModal(null);
      setConfirmText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setBulkBusy(false);
    }
  };
  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-overline text-[#E63946]">Players</div>
            <h1 data-testid="admin-players-title" className="font-display text-4xl uppercase tracking-tighter mt-3">
              Player Database
            </h1>
            <p className="text-xs text-slate-500 mt-1">{total} players · admin internal view</p>
          </div>
          <button
            data-testid="admin-new-player-button"
            onClick={() => { setForm(EMPTY_FORM); setShowForm(true); setError(""); }}
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm"
          >
            <Plus className="w-4 h-4" /> Add Player
          </button>
        <div className="mt-3">
          <button
            data-testid="export-players-csv"
            onClick={() => {
              const params = {};
              Object.entries(filters).forEach(([k, v]) => {
                if (v === "" || v === null) return;
                if (k === "status") params.archived = v === "archived";
                else if (k === "graduation_year") params.graduation_year = Number(v);
                else params[k] = v;
              });
              downloadFile("/admin/players/export.csv", params, "cbr-players.csv")
                .then(() => toast.success("Players CSV exported"))
                .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Export failed"));
            }}
            className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        </div>

        {/* Filters */}
        <div className="mt-6 bg-white border border-slate-200 p-4 rounded-sm grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="admin-players-search"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Name, school, team, email…"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <input
            type="number"
            placeholder="Grad year"
            value={filters.graduation_year}
            onChange={(e) => setFilters({ ...filters, graduation_year: e.target.value })}
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
          <select value={filters.primary_position} onChange={(e) => setFilters({ ...filters, primary_position: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All Positions</option>
            {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filters.province} onChange={(e) => setFilters({ ...filters, province: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All Provinces</option>
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filters.camp_id} onChange={(e) => setFilters({ ...filters, camp_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All Camps</option>
            {camps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
          {[
            { label: "Active", val: "active" },
            { label: "Archived", val: "archived" },
            { label: "All", val: "all" },
          ].map((s) => (
            <button
              key={s.val}
              data-testid={`players-status-${s.val}`}
              onClick={() => setFilters({ ...filters, status: s.val })}
              className={`px-3 py-1 font-semibold uppercase tracking-wider rounded-sm ${filters.status === s.val ? "bg-[#0A192F] text-white" : "bg-white border border-slate-300"}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div data-testid="bulk-action-bar" className="mt-4 bg-[#0A192F] text-white rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap">
            <span data-testid="bulk-selected-count" className="text-sm font-semibold">{selected.size} selected</span>
            <button data-testid="bulk-clear" onClick={clearSelection} className="text-xs uppercase tracking-wider text-white/70 hover:text-white underline">Clear</button>
            <div className="flex-1" />
            <button data-testid="bulk-archive" disabled={bulkBusy} onClick={() => bulkArchive(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
              <Archive className="w-3.5 h-3.5" /> Archive Selected
            </button>
            <button data-testid="bulk-restore" disabled={bulkBusy} onClick={() => bulkArchive(false)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
              <RotateCcw className="w-3.5 h-3.5" /> Restore Selected
            </button>
            <button data-testid="bulk-delete" disabled={bulkBusy} onClick={openDeletePreview} className="inline-flex items-center gap-1.5 bg-[#E63946] hover:bg-[#D90429] px-3 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
              <Trash2 className="w-3.5 h-3.5" /> Delete Selected
            </button>
          </div>
        )}

        {/* Table */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-3 w-10">
                  <button data-testid="select-all-players" onClick={toggleAll} className="align-middle" title={allSelected ? "Clear all" : "Select all"}>
                    {allSelected ? <CheckSquare className="w-4 h-4 text-[#E63946]" /> : <Square className="w-4 h-4 text-slate-400" />}
                  </button>
                </th>
                <th className="text-left p-3 label-overline">Name</th>
                <th className="text-left p-3 label-overline">Class</th>
                <th className="text-left p-3 label-overline">Pos</th>
                <th className="text-left p-3 label-overline">B/T</th>
                <th className="text-left p-3 label-overline">Province</th>
                <th className="text-left p-3 label-overline">School</th>
                <th className="text-left p-3 label-overline">Visibility</th>
                <th className="text-right p-3 label-overline">Manage</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${selected.has(p.id) ? "bg-red-50/40" : ""}`}>
                  <td className="p-3">
                    <button data-testid={`select-player-${p.id}`} onClick={() => toggleOne(p.id)} className="align-middle">
                      {selected.has(p.id) ? <CheckSquare className="w-4 h-4 text-[#E63946]" /> : <Square className="w-4 h-4 text-slate-300 hover:text-slate-500" />}
                    </button>
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/players/${p.id}`} data-testid={`admin-player-link-${p.id}`} className="font-semibold hover:text-[#E63946]">
                      {p.first_name} {p.last_name}
                    </Link>
                    {p.archived && <span className="ml-2 bg-slate-700 text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">Archived</span>}
                  </td>
                  <td className="p-3 font-data">{p.graduation_year || "—"}</td>
                  <td className="p-3 font-data">{p.primary_position || "—"}</td>
                  <td className="p-3 text-xs">{p.bats || "—"}/{p.throws || "—"}</td>
                  <td className="p-3">{p.province || "—"}</td>
                  <td className="p-3">{p.school || "—"}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${p.public_visible ? "bg-emerald-100 text-emerald-800" : "bg-slate-200"}`}>
                      {p.public_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.public_visible ? "Public" : "Hidden"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      data-testid={`featured-toggle-${p.id}`}
                      onClick={() => toggleFeatured(p)}
                      title={p.is_featured ? "Featured — click to remove" : "Mark as Featured"}
                      className="inline-flex items-center mr-3 align-middle"
                    >
                      <Star className={`w-4 h-4 ${p.is_featured ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
                    </button>
                    <Link to={`/admin/players/${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:text-[#0A192F] align-middle">
                      <QrCode className="w-3.5 h-3.5" /> Manage
                    </Link>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="9" className="p-8 text-center text-sm text-slate-500">No players match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="font-display text-2xl uppercase tracking-tight">Add Player</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-2 text-sm text-[#E63946]">{error}</div>}
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ["first_name", "First Name"],
                  ["last_name", "Last Name"],
                  ["graduation_year", "Grad Year"],
                  ["height_cm", "Height (cm)"],
                  ["weight_kg", "Weight (kg)"],
                  ["school", "School"],
                  ["city", "City"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["profile_photo_url", "Profile Photo URL"],
                ].map(([key, label]) => (
                  <div key={key} className={key === "profile_photo_url" ? "sm:col-span-2" : ""}>
                    <label className="label-overline">{label}</label>
                    <input data-testid={`new-player-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  </div>
                ))}
                <div>
                  <label className="label-overline">Primary Position</label>
                  <select data-testid="new-player-primary_position" value={form.primary_position} onChange={(e) => setForm({ ...form, primary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                    <option value="">—</option>
                    {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Secondary Position</label>
                  <select data-testid="new-player-secondary_position" value={form.secondary_position} onChange={(e) => setForm({ ...form, secondary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                    <option value="">— None —</option>
                    {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Province</label>
                  <select value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                    <option value="">—</option>
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Bats</label>
                  <select value={form.bats} onChange={(e) => setForm({ ...form, bats: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                    <option value="">—</option><option value="L">L</option><option value="R">R</option><option value="S">S</option>
                  </select>
                </div>
                <div>
                  <label className="label-overline">Throws</label>
                  <select value={form.throws} onChange={(e) => setForm({ ...form, throws: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                    <option value="">—</option><option value="L">L</option><option value="R">R</option>
                  </select>
                </div>
                <div>
                  <label className="label-overline">Date of Birth <span className="text-slate-400 normal-case">(private)</span></label>
                  <input data-testid="new-player-date_of_birth" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-overline">Current Team</label>
                  <div className="mt-1"><TeamSelector value={form.current_team_id} onChange={(t) => setForm({ ...form, current_team_id: t?.id || "" })} testidPrefix="admin-np-team" /></div>
                </div>
                <label className="sm:col-span-2 flex items-center gap-2 text-xs text-slate-600">
                  <input data-testid="new-player-show_age" type="checkbox" checked={form.show_age_publicly} onChange={(e) => setForm({ ...form, show_age_publicly: e.target.checked })} />
                  Show age (not birthdate) on public profile. Off by default; full DOB is never public.
                </label>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button
                data-testid="admin-create-player-save"
                onClick={create}
                disabled={busy}
                className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
              >
                {busy ? "Saving…" : "Create Player"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div data-testid="delete-confirm-modal" className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-display text-2xl uppercase tracking-tight text-[#E63946] flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Delete Players</h2>
              <button onClick={() => setDeleteModal(null)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-700">
                You are about to <strong>permanently delete {deleteModal.count} player{deleteModal.count === 1 ? "" : "s"}</strong>. This cannot be undone and will remove all linked records below.
              </p>
              {deleteModal.with_history > 0 && (
                <div className="border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-800">
                  <strong>{deleteModal.with_history}</strong> of these have meaningful history (payments, registrations, rosters, evaluations, awards or live stats). Consider <strong>Archive</strong> instead to preserve their history.
                </div>
              )}
              <div className="border border-slate-200 rounded-sm max-h-56 overflow-y-auto divide-y divide-slate-100">
                {deleteModal.items.map((i) => {
                  const links = Object.entries(i.links).filter(([k, v]) => !["has_meaningful_history"].includes(k) && v);
                  return (
                    <div key={i.id} data-testid={`delete-preview-${i.id}`} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{i.name} <span className="text-[11px] text-slate-400 font-normal">{i.graduation_year || ""} {i.province || ""}</span></div>
                        {i.has_meaningful_history
                          ? <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">Has history</span>
                          : <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">Test/empty</span>}
                      </div>
                      {links.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {links.map(([k, v]) => (
                            <span key={k} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{k.replace(/_/g, " ")}: {v}</span>
                          ))}
                        </div>
                      ) : <div className="mt-1 text-[10px] text-slate-400">No linked records</div>}
                    </div>
                  );
                })}
              </div>
              <div>
                <label className="label-overline">Type <span className="text-[#E63946]">DELETE</span> to confirm</label>
                <input
                  data-testid="delete-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setDeleteModal(null)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button
                data-testid="delete-confirm-button"
                onClick={confirmDelete}
                disabled={bulkBusy || confirmText !== "DELETE"}
                className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> {bulkBusy ? "Deleting…" : `Delete ${deleteModal.count}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
